import type { IChapter } from "@lexicanon/shared"
import { recordingModeOf } from "@lexicanon/shared"
import { useLingui } from "@lingui/react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useMemo } from "react"
import {
  MeetingPlayerProvider,
  useMeetingPlayerController,
} from "../../../components/MeetingSummary/MeetingPlayerContext"
import { Player } from "../../../components/Player"
import { SpeakerStatusBadges } from "../../../components/SpeakerChip/SpeakerStatusBadges"
import { SpeakerEditModal } from "../../../components/SpeakerEditModal"
import { displayMeetingTitle } from "../../../lib/meeting-title"
import { normalizeSegmentSpeakers, speakerLabelDisplayMap } from "../../../lib/speaker-mapping"
import { speakerDisplayNameList } from "../../../lib/speaker-vm"
import { TranscriptView } from "../-transcript/TranscriptView"
import { useTranscriptPage } from "../-transcript/useTranscriptPage"

function isChapter(value: unknown): value is IChapter {
  if (typeof value !== "object" || value === null) return false
  if (!("title" in value) || !("description" in value) || !("start_offset" in value) || !("end_offset" in value)) {
    return false
  }
  return (
    typeof value.title === "string" &&
    typeof value.description === "string" &&
    typeof value.start_offset === "number" &&
    typeof value.end_offset === "number"
  )
}

function chapterList(value: unknown): IChapter[] | undefined {
  if (!Array.isArray(value)) return undefined
  const chapters = value.filter(isChapter)
  return chapters.length > 0 ? chapters : undefined
}

/** Chapters from a translated analysis when it has them, otherwise the meeting's own. */
function readMeetingChapters(meeting: unknown): IChapter[] | undefined {
  if (typeof meeting !== "object" || meeting === null) return undefined
  if ("analysis" in meeting) {
    const analysis = meeting.analysis
    if (typeof analysis === "object" && analysis !== null && "chapters" in analysis) {
      const fromAnalysis = chapterList(analysis.chapters)
      if (fromAnalysis) return fromAnalysis
    }
  }
  if ("chapters" in meeting) return chapterList(meeting.chapters)
  return undefined
}

function TranscriptPage(): React.JSX.Element {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { i18n } = useLingui()
  const page = useTranscriptPage(id, { enrichSegments: true })
  const meeting = page.meeting
  const recordingUrl = meeting?.recording_url
  const { controls, playerRef, onPlayingChange, onProgress, onSpeedChange } = useMeetingPlayerController(
    Boolean(recordingUrl),
  )
  const speakers = page.speakerVMs.speakers
  const recordedMode = recordingModeOf(meeting?.template_id)
  const labelMap = useMemo(() => speakerLabelDisplayMap(speakers), [speakers])
  const speakerNames = useMemo(() => speakerDisplayNameList(speakers, i18n), [speakers, i18n])
  const segments = useMemo(
    () => normalizeSegmentSpeakers(meeting?.streaming_segments, labelMap.size > 0 ? labelMap : speakerNames),
    [labelMap, meeting?.streaming_segments, speakerNames],
  )
  const chapters = useMemo(() => readMeetingChapters(meeting), [meeting])
  const heading = meeting
    ? displayMeetingTitle(
        meeting.subject,
        meeting.topics,
        i18n,
        recordedMode === "lite" ? { lite: true, startedAt: meeting.start_time } : undefined,
      )
    : undefined
  const onMeetingTab = useCallback((): void => {
    void navigate({ to: "/meetings/$id", params: { id } })
  }, [id, navigate])

  const player =
    recordingUrl != null && recordingUrl.length > 0 ? (
      <Player
        ref={playerRef}
        src={recordingUrl}
        segments={segments}
        speakerColors={page.speakerVMs.colorMap}
        speakerNames={speakerNames}
        speakers={speakers}
        chapters={chapters}
        showChaptersWhenEmpty={recordedMode === "full"}
        showLegend={false}
        onPlayingChange={onPlayingChange}
        onProgress={onProgress}
        onSpeedChange={onSpeedChange}
      />
    ) : null

  return (
    <MeetingPlayerProvider value={controls}>
      <TranscriptView
        {...page.viewProps}
        heading={heading}
        showMeetingTabs={recordedMode === "full"}
        onMeetingTab={onMeetingTab}
        player={player}
      />
      <SpeakerEditModal
        meetingId={id}
        opened={page.editingSpeaker !== null}
        speaker={page.editingSpeaker}
        allSpeakers={page.speakerVMs.speakers}
        quotes={page.editingSpeaker ? (page.quotesMap.get(page.editingSpeaker.displayName) ?? []) : []}
        profile={page.editingProfile}
        speakerSegments={
          page.editingSpeaker?.label ? page.speakerSegmentsMap.get(page.editingSpeaker.label) : undefined
        }
        audioUrl={page.meeting?.recording_url}
        titleBadges={
          page.editingSpeaker ? (
            <SpeakerStatusBadges
              status={page.editingSpeaker.status}
              recognitionSource={page.editingSpeaker.recognitionSource}
            />
          ) : null
        }
        onClose={page.closeSpeakerEdit}
        onEdited={page.onSpeakerEdited}
      />
    </MeetingPlayerProvider>
  )
}

export const Route = createFileRoute("/_auth/meetings/$id_/transcript")({
  component: TranscriptPage,
})
